// Sentry MUST init before render so it can install window.onerror +
// unhandled-rejection handlers before any app code throws. No-op when
// VITE_SENTRY_DSN isn't set at build time.
import { initSentry } from './lib/sentry';
initSentry();

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { AuthProvider } from './auth/AuthContext';
import { PrivacyPolicy, TermsOfService, SupportPage, CopyrightPage } from './components/LegalPage';
import { MarketingLanding } from './components/MarketingPage';
import { CustomPuzzlePlay } from './components/CustomPuzzlePlay';
import { PuzzleDesigner } from './components/PuzzleDesigner';
import { PuzzleBrowse } from './components/PuzzleBrowse';
import { AdminDashboard } from './components/AdminDashboard';
import { UserProfile } from './components/UserProfile';
import { ResetPasswordPage } from './components/ResetPasswordPage';
import { VerifyEmailPage } from './components/VerifyEmailPage';
import { CompleteSignupPage } from './components/CompleteSignupPage';
import './styles.css';

// Top-level routing. nginx falls back to index.html for unmatched
// paths, so /privacy, /terms, and /p/:id all land here; we hand each
// to a focused root component without mounting the daily-puzzle App
// reducer. Pure HTML + the wordmark.
function pickRoot() {
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';

  // Theme rehydration outside App.tsx: legal pages, /p/:id play surface,
  // and any future static route runs before App's theme effect, so set
  // the html class directly from localStorage so they don't flash light
  // for dark-mode users.
  try {
    const saved = localStorage.getItem('hm.theme');
    if (saved === 'dark') document.documentElement.classList.add('dark');
  } catch { /* ignore */ }

  const isLegal = path === '/privacy'   || path === '/privacy/'
               || path === '/terms'     || path === '/terms/'
               || path === '/support'   || path === '/support/'
               || path === '/copyright' || path === '/copyright/';
  if (isLegal) {
    // Lift the game's `body { overflow: hidden }` so the legal page
    // can scroll naturally.
    document.body.classList.add('hm-legal');
    if (path.startsWith('/privacy'))   return <PrivacyPolicy />;
    if (path.startsWith('/support'))   return <SupportPage />;
    if (path.startsWith('/copyright')) return <CopyrightPage />;
    return <TermsOfService />;
  }

  // /app — marketing landing page. Linked from Apple App Store Connect
  // "Marketing URL" field. Same body-scroll lift as the legal pages
  // since it's a long-form scrolling page, not the puzzle UI.
  if (path === '/app' || path === '/app/') {
    document.body.classList.add('hm-legal');
    return <MarketingLanding />;
  }

  // /p/:id — shared custom puzzle. Slug is the 10-char base64url id we
  // stamp at publish time. Auth is optional (anonymous can play).
  const customMatch = /^\/p\/([A-Za-z0-9_-]{10})\/?$/.exec(path);
  if (customMatch) {
    const id = customMatch[1];
    return (
      <AuthProvider>
        <CustomPuzzlePlay id={id} />
      </AuthProvider>
    );
  }

  // /designer — premium-only puzzle authoring surface. AuthProvider
  // mounts so PuzzleDesigner can read auth.user.isPremium directly.
  // Also lifts the body overflow lock so the designer can scroll.
  if (path === '/designer' || path === '/designer/') {
    document.body.classList.add('hm-designer-open');
    return (
      <AuthProvider>
        <PuzzleDesigner />
      </AuthProvider>
    );
  }

  // /browse — public discovery feed.
  if (path === '/browse' || path === '/browse/') {
    // Lift the game's body-overflow lock so the list scrolls.
    document.body.classList.add('hm-page-scroll');
    return (
      <AuthProvider>
        <PuzzleBrowse />
      </AuthProvider>
    );
  }

  // /admin — admin dashboard (Overview / Users / Reports tabs).
  // Role check is server-side; the page renders a friendly gate for
  // anonymous / non-admin visitors.
  if (path === '/admin' || path === '/admin/') {
    document.body.classList.add('hm-page-scroll');
    return (
      <AuthProvider>
        <AdminDashboard />
      </AuthProvider>
    );
  }

  // /reset-password — landing for the password-reset email link.
  // Stateless: reads ?token=X and posts to /auth/reset-password.
  if (path === '/reset-password' || path === '/reset-password/') {
    document.body.classList.add('hm-page-scroll');
    return (
      <AuthProvider>
        <ResetPasswordPage />
      </AuthProvider>
    );
  }

  // /verify-email — landing for the email-verification link.
  // Stateless: reads ?token=X and posts to /auth/verify-email on mount.
  if (path === '/verify-email' || path === '/verify-email/') {
    document.body.classList.add('hm-page-scroll');
    return (
      <AuthProvider>
        <VerifyEmailPage />
      </AuthProvider>
    );
  }

  // /complete-signup — second leg of the Google/Apple OAuth flow when
  // the user is brand-new. The /oauth/{provider}/callback route sets a
  // signed pending-signup cookie and redirects here so the user can
  // pick their display name.
  if (path === '/complete-signup' || path === '/complete-signup/') {
    document.body.classList.add('hm-page-scroll');
    return (
      <AuthProvider>
        <CompleteSignupPage />
      </AuthProvider>
    );
  }

  // /u/:displayName — public user profile + their puzzles. Display
  // names are URL-encoded, kept simple-ASCII at the route layer.
  const profileMatch = /^\/u\/([^/]+)\/?$/.exec(path);
  if (profileMatch) {
    const name = decodeURIComponent(profileMatch[1]);
    // Lift the game's body-overflow lock so the profile scrolls.
    document.body.classList.add('hm-page-scroll');
    return (
      <AuthProvider>
        <UserProfile displayName={name} />
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {pickRoot()}
  </StrictMode>
);

// Fire-and-forget anonymous-visit ping. Powers the admin dashboard's
// "web" daily count by minting/refreshing an hp_vid cookie + writing
// one SiteVisit row per (visitor, day in NY). Auth-independent, so it
// covers signed-out viewers too. Deferred behind a short timeout so
// it never competes with the initial render or font fetch.
if (typeof window !== 'undefined') {
  setTimeout(() => {
    fetch('/api/v1/track/visit', {
      method: 'POST',
      credentials: 'include',
    }).catch(() => { /* analytics is best-effort */ });
  }, 250);
}
